import { PlusCircleFilled } from '@ant-design/icons';
import { filter, find, toUpper } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EPCheckbox, EPCollapse } from '@/comp';
import EPTierTooltip from '@/comp/common/EPTierTooltip';
import { permissions } from '@/constants';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import { useInvestigationMetricSelection } from '@/hooks/useInvestigationMetricSelection';
import { checkTierPermission, toTranslationFormat } from '@/utils/util-functions';

import { ShowInDeviceStatusContainer, TierProtectionPanel } from './styled';

const { EPPanel } = EPCollapse;

const metricHasSourceName = metricName =>
  [toUpper('flowM3Min'), toUpper('estimatedFlowM3Min'), toUpper('specificPower')].includes(toUpper(metricName));

const DevicesCollapsable = canSelectMetric => {
  const { devicesStore, investigationStore, permissionStore, stationStore } = useStores();
  const { t } = useTranslation();
  const { selectDeviceMetric } = useInvestigationMetricSelection();
  const [isTierRequestConfirmationModalOpen, setIsTierRequestConfirmationModalOpen] = useState(false);
  const shouldRestrictInvestigationDashboard = checkTierPermission(
    ComponentTierName.InvestigationDashboard,
    stationStore,
  );

  const hasEnhancedIDViewPermission = permissionStore.isAuth(permissions.VIEW_ENHANCED_INVESTIGATION_DASHBOARD);

  const extra = device => {
    const totalSelected = filter(device.metrics, { isSelected: true }).length;
    return (
      <span className="selected-counter">
        {totalSelected}/{device.metrics.length}
      </span>
    );
  };
  const trimmedFlowSourceName = new Map([
    [toUpper('Sensor Based'), 'Sensor Based'],
    [toUpper('Compressor Estimation'), 'Comp. Est.'],
    [toUpper('kW Based Calculation'), 'kW Calc. Based'],
    [toUpper('AI Calculation'), 'AI Calc.'],
  ]);

  const isEligibleForInvestigation = deviceTypes => {
    return !!find(deviceTypes, type =>
      [...compressorTypes, DeviceType.Pump, DeviceType.General, DeviceType.Vacuum].includes(type),
    );
  };

  const isCompressor = deviceTypes => {
    return !!find(deviceTypes, type => compressorTypes.includes(type));
  };

  const hasAdditionalSensors = devicesStore.devicesMetadataWithoutPipes?.some(
    device => device?.deviceTypes && !isCompressor(device.deviceTypes),
  );

  const getMetricSource = metricName => {
    if (!metricName.startsWith('estimated')) return t(trimmedFlowSourceName.get(toUpper('Sensor Based')));

    return t(trimmedFlowSourceName.get(toUpper('kW based calculation')));
  };

  const onChangeChecked = (e, metricId) => {
    if (!canSelectMetric(e)) return;
    selectDeviceMetric(metricId, e.target.checked);
  };

  const onSelectInDeviceStatusChange = deviceId => e => {
    investigationStore.setSelectedInDeviceStatus(deviceId, e?.target?.checked);
  };

  const onClickHereHandler = () => {
    stationStore.updateRequestTier({
      emailRequestTypeOfInterestedPackage: interestedPackageEnum.ADDITIONAL_SENSORS,
      emailRequestButtonClicked: '-+ button',
      emailRequestWhichScreen: 'Investigation',
    });
    setIsTierRequestConfirmationModalOpen(true);
  };

  return (
    <>
      {investigationStore.sortedDevicesMetrics
        .filter(device =>
          shouldRestrictInvestigationDashboard ? isEligibleForInvestigation(device.deviceTypes) : true,
        )
        .map((device, index) => (
          <EPPanel key={index + 1} header={device.name} extra={extra(device)} data-device-id={device.id}>
            {device.metrics.map(metric => (
              <div key={metric.id}>
                <EPCheckbox checked={metric.isSelected} onChange={e => onChangeChecked(e, metric.id)}>
                  <>
                    {metric.deviceName !== device.name && `${metric.deviceName} | `}
                    {t(toTranslationFormat(devicesStore.getMetricName(metric.name)))}
                    {isCompressor(device.deviceTypes) &&
                      metricHasSourceName(metric.name) &&
                      `(${getMetricSource(metric.name)})`}
                  </>
                </EPCheckbox>
              </div>
            ))}
            {hasEnhancedIDViewPermission && devicesStore.isCompressor(device) && (
              <ShowInDeviceStatusContainer>
                <div className="horizontal-line" />
                <EPCheckbox
                  checked={device?.isSelectedInDeviceStatus}
                  onChange={onSelectInDeviceStatusChange(device?.id)}
                  className="show-in-device-status-checkbox"
                >
                  {t('showInDeviceStatus')}
                </EPCheckbox>
              </ShowInDeviceStatusContainer>
            )}
          </EPPanel>
        ))}
      {shouldRestrictInvestigationDashboard && hasAdditionalSensors && (
        <TierProtectionPanel
          header={t('additionalSensors')}
          extra={
            <EPTierTooltip
              textPart2={t('addIndividualSensors')}
              tooltipPlacement="bottom"
              tooltipWidth={330}
              initialIsTierRequestModalOpen={isTierRequestConfirmationModalOpen}
              emailRequestTypeOfInterestedPackage={interestedPackageEnum.PRESSURE_DROP}
              emailRequestButtonClicked={'additional Sensors tooltip '}
              emailRequestWhichScreen={'Investigation'}
            >
              <div onClick={onClickHereHandler} className="tier-plus-icon">
                <PlusCircleFilled />
              </div>
            </EPTierTooltip>
          }
          collapsible="disabled"
          $shouldRestrictInvestigationDashboard={shouldRestrictInvestigationDashboard}
        />
      )}
    </>
  );
};

export default DevicesCollapsable;
